import { push, ref, remove, update } from "firebase/database";

import { database } from "../../firebase/firebase";
import {
  ConfigFormValues,
  Extra,
  Place,
  PricingData,
  Route,
  Zone,
  buildSeedUpdates,
  configSchema,
  extraSchema,
  placeSchema,
  routeSchema,
  zoneSchema,
} from "../../lib/pricing";

export type PricingCollection = "zones" | "places" | "routes" | "extras";

const collectionRef = (collection: PricingCollection) => ref(database, `pricing/${collection}`);
const itemRef = (collection: PricingCollection, id: string) =>
  ref(database, `pricing/${collection}/${id}`);

/** O RTDB rejeita `undefined`; `null` remove a chave no update. */
const stripUndefined = <T extends object>(value: T): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(value).map(([key, v]) => [key, v === undefined ? null : v])
  );

/** Grava (cria ou atualiza) um item já validado. Nunca usa `set` no nó raiz. */
async function save<T extends object>(collection: PricingCollection, value: T, id?: string) {
  const payload = stripUndefined(value);
  if (id) {
    await update(itemRef(collection, id), payload);
    return id;
  }
  const created = await push(collectionRef(collection), payload);
  return created.key as string;
}

export const saveZone = (raw: unknown, id?: string) => save<Zone>("zones", zoneSchema.parse(raw), id);
export const savePlace = (raw: unknown, id?: string) =>
  save<Place>("places", placeSchema.parse(raw), id);
export const saveRoute = (raw: unknown, id?: string) =>
  save<Route>("routes", routeSchema.parse(raw), id);
export const saveExtra = (raw: unknown, id?: string) =>
  save<Extra>("extras", extraSchema.parse(raw), id);

export const deleteItem = (collection: PricingCollection, id: string) =>
  remove(itemRef(collection, id));

/** Dependências que impedem excluir uma região. */
export const zoneDependencies = (data: PricingData, zoneId: string) => {
  const places = Object.values(data.places).filter((p) => p.zoneId === zoneId).length;
  const routes = Object.values(data.routes).filter(
    (r) => r.originZoneId === zoneId || r.destinationZoneId === zoneId
  ).length;
  return { places, routes, blocked: places > 0 || routes > 0 };
};

/** Tarifas que apontam para um local (impedem excluir o local). */
export const placeDependencies = (data: PricingData, placeId: string) => {
  const routes = Object.values(data.routes).filter(
    (r) => r.originPlaceId === placeId || r.destinationPlaceId === placeId
  ).length;
  return { routes, blocked: routes > 0 };
};

export const saveConfig = async (raw: ConfigFormValues) => {
  const config = configSchema.parse(raw);
  await update(ref(database, "pricing/config"), { ...config, updatedAt: Date.now() });
};

export const setZoneOrder = (orders: Record<string, number>) => {
  const updates: Record<string, number> = {};
  Object.entries(orders).forEach(([id, order]) => {
    updates[`${id}/order`] = order;
  });
  return update(collectionRef("zones"), updates);
};

export const setActive = (collection: PricingCollection, id: string, active: boolean) =>
  update(itemRef(collection, id), { active });

/** Carrega a tabela de exemplo (apenas quando não há regiões). */
export const seedPricing = () => update(ref(database, "pricing"), buildSeedUpdates());
